import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';

import AppLoader from 'Src/components/AppLoader';
import Copy from 'Src/components/Copy';
import { Flex } from 'Src/components/layout/Flex';
import { CheckPaymentDef } from 'Src/feature/check/models';
import { Modal } from 'Src/feature/modal/Modal';
import { ModalAction } from 'Src/feature/modal/ModalAction';
import { ModalActions } from 'Src/feature/modal/ModalActions';
import { ModalContent } from 'Src/feature/modal/ModalContent';
import { ModalHeader } from 'Src/feature/modal/ModalHeader';
import { ModalTitle } from 'Src/feature/modal/ModalTitle';

import { PaymentIcon } from './PaymentIcon';
import { usePaymentCompleteSummary } from './usePaymentCompleteSummary';

const sectionId = 'PaymentCompleteModal';

interface PaymentCompleteModalProps {
  open: boolean;
  payment?: CheckPaymentDef;
  showGitCardBalance?: boolean;
  onClose?: () => void;
  onVoid?: () => void;
  onEditAmount?: () => void;
  onReceipt?: () => void;
  isProcessing?: boolean;
  ['data-testid']?: string;
}

const PaymentCompleteModalContent: FC<PaymentCompleteModalProps> = ({
  payment,
  showGitCardBalance,
  onClose,
  onVoid,
  onEditAmount,
  onReceipt,
  isProcessing,
}) => {
  const { t } = useTranslation();
  const paymentSummary = usePaymentCompleteSummary({ payment });

  return (
    <>
      <ModalHeader>
        <ModalTitle copy={t('payment.labels.paymentComplete')} />
      </ModalHeader>
      <ModalContent flex={{ alignItems: 'center', justifyContent: 'center' }}>
        {
          isProcessing
            ? <AppLoader />
            : (
              <>
                <Copy variant="h4" value={payment?.amountAppliedToCheck} type="currency" align="center" />
                <Flex horizontal gap="xs" justifyContent="center" alignItems="center">
                  <PaymentIcon paymentType={payment?.paymentType} size="sm" />
                  <Copy variant="body" value={paymentSummary} />
                </Flex>
                {showGitCardBalance && <Copy variant="body" value={t('payment.labels.remainingGiftCardBalance', { balance: payment?.giftCardData?.balance })} />}
              </>
            )
        }
      </ModalContent>
      <ModalActions>
        <ModalAction label={t('labels.void')} variant="outlined" onClick={onVoid} />
        {(payment?.paymentType === 'CC' || payment?.paymentType === 'GiftCard') && !!onEditAmount ? (
          <ModalAction label={t('payment.labels.editAmount')} color="secondary" onClick={onEditAmount} />
        ) : (
          <ModalAction label={t('labels.done')} color="secondary" onClick={onClose} />
        )}
        <ModalAction label={t('labels.receipt')} color="primary" onClick={onReceipt} />
      </ModalActions>
    </>
  );
};

// eslint-disable-next-line react/no-multi-comp
const PaymentCompleteModal: FC<PaymentCompleteModalProps> = (props) => (
  <Modal
    data-testid={props['data-testid'] ?? sectionId}
    open={props.open}
    onBackDropClick={props.onClose}
  >
    <PaymentCompleteModalContent {...props} />
  </Modal>
);

export { PaymentCompleteModal, PaymentCompleteModalContent, PaymentCompleteModalProps };
